/**
 * Business-email signup enforcement lives in the database: a BEFORE INSERT trigger on auth.users
 * rejects blocked domains (consumer webmail, organizations we don't serve, and academic
 * addresses) on every account-creation path (see
 * supabase/schemas/037-business-email-signups.sql). This module holds only the user-facing pieces —
 * the rejection copy, the contact link, and structured logging — plus a small domain helper. The
 * disallowed-domain list and the allow/deny logic are NOT duplicated here; the server actions ask
 * the DB (`is_email_domain_blocked`) to decide whether to show the message.
 */

/** Copy shown wherever a personal-email signup is rejected. Pending product review (Joe/Peter). */
export const BUSINESS_EMAIL_REQUIRED_MESSAGE =
  'Geoforge requires a business email address. Please sign up with your work email, not a personal one.';

/**
 * Contact for a rejected signup. We surface the address as visible text (linked via `mailto:`)
 * rather than a bare "Contact us" link: the webapp has no live chat yet (a later phase) and a
 * `mailto:` silently does nothing on a browser without a mail handler, so the reader must be able
 * to see and copy the address. hello@geoforge.ai matches the landing footer and upgrade CTAs.
 */
export const BUSINESS_EMAIL_CONTACT_ADDRESS = 'hello@geoforge.ai';
export const BUSINESS_EMAIL_CONTACT_URL = `mailto:${BUSINESS_EMAIL_CONTACT_ADDRESS}?subject=${encodeURIComponent(
  'Geoforge signup — business email required'
)}`;

/** Lowercased domain portion of an email (or bare domain), or null if it has no `@` / domain. */
export function getEmailDomain(emailOrDomain: string | undefined | null): string | null {
  if (!emailOrDomain) return null;
  const trimmed = emailOrDomain.trim().toLowerCase();
  const domain = trimmed.includes('@') ? trimmed.slice(trimmed.lastIndexOf('@') + 1) : trimmed;
  return domain || null;
}

/**
 * Records a rejected signup so we can review how often the business-email rule fires and whether
 * it's catching real customers. Domain only — never the full address (PII). Emitted to stdout,
 * which Vercel forwards to Datadog.
 */
export function logBusinessEmailRejection(params: {
  provider: 'password' | 'google';
  emailDomain: string | null;
}): void {
  console.warn(
    '[auth] business_email_rejected',
    JSON.stringify({ event: 'business_email_rejected', ...params })
  );
}
